// Ported from sn-plugin2 apps/playground/src/{setup/setup,settings/ThemeGroup}.ts(x) @ f71f2180 and packages/design-tokens/src/mode.ts @ 54685d64 — keep in step with the plugin.
import { relativeLuminance } from '../../../../utils/colorContrast'
import {
  PLUGIN_AUTO_DARK_BELOW_LUMINANCE,
  PLUGIN_BASE_COLOURS,
  PLUGIN_BRACKET_START_COLOUR,
  PLUGIN_BRAND_ACCENT,
  PLUGIN_COLOUR_START_FROM,
  PLUGIN_OPTIONAL_COLOUR_GROUPS,
  PLUGIN_PAGE_COLOUR_GROUPS,
  PLUGIN_THEME_PRESETS,
  PLUGIN_VIEW_TABS,
} from '../constants/embedOptions'
import type {
  PluginBrand,
  PluginThemePresetName,
  PluginEmbedSettings,
  PluginOptionalColour,
  PluginPageColour,
  PluginThemeColour,
  PluginView,
} from '../types/embedSettings'

type PluginColours = Partial<Record<PluginThemeColour, string>>
type PluginResolvedMode = 'light' | 'dark'

const OPTIONAL_COLOUR_NAMES: PluginOptionalColour[] =
  PLUGIN_OPTIONAL_COLOUR_GROUPS.flatMap((group) =>
    group.colours.map(({ name }) => name)
  )

const PAGE_COLOUR_NAMES: PluginPageColour[] = PLUGIN_PAGE_COLOUR_GROUPS.flatMap(
  (group) => group.colours.map(({ name }) => name)
)

/** Switches the view; an initial tab the new view lacks goes back to Default, and the entity id is cleared. */
export const withPluginView = (
  settings: PluginEmbedSettings,
  view: PluginView
): PluginEmbedSettings => {
  if (settings.view === view) {
    return settings
  }
  const keepsTab = PLUGIN_VIEW_TABS[view].some(
    (tab) => tab.id === settings.initialTab
  )
  return {
    ...settings,
    view,
    entityId: '',
    initialTab: keepsTab ? settings.initialTab : '',
  }
}

/** Shows or hides one tab of one view, keeping the other views' tabs; a key the view lacks is ignored. */
export const setPluginTabHidden = (
  hidden: Record<PluginView, string[]>,
  view: PluginView,
  key: string,
  isHidden: boolean
): Record<PluginView, string[]> => {
  const current = hidden[view]
  const next = isHidden
    ? [...current, key]
    : current.filter((other) => other !== key)
  const wanted = new Set(next)
  return {
    ...hidden,
    [view]: PLUGIN_VIEW_TABS[view]
      .map((tab) => tab.key)
      .filter((tabKey) => wanted.has(tabKey)),
  }
}

/** The mode the embed's Auto picks: dark for a dark background colour, else light. */
export const pluginAutoMode = (
  background: string | undefined
): PluginResolvedMode =>
  background !== undefined &&
  relativeLuminance(background) < PLUGIN_AUTO_DARK_BELOW_LUMINANCE
    ? 'dark'
    : 'light'

/** What the plugin paints for each page colour while the host leaves it unset. */
export const pluginPageDefaults = (
  brand: PluginBrand,
  settings: PluginEmbedSettings
): Record<PluginPageColour, string> => {
  const mode =
    settings.mode === 'auto'
      ? pluginAutoMode(settings.colours.background)
      : settings.mode
  const base = PLUGIN_BASE_COLOURS[mode]
  return {
    background: base.bg,
    text: base.ink,
    headerBackground: base.bg,
    headerText: base.ink,
    primary: PLUGIN_BRAND_ACCENT[brand],
    border: base.line,
  }
}

/** The colour an optional colour's swatch starts from: a page colour of the theme, else the plugin's default for it. */
export const pluginStartColour = (
  colours: PluginColours,
  name: PluginOptionalColour,
  defaults: Record<PluginPageColour, string>
): string => {
  if (name === 'bracketBackground') {
    return PLUGIN_BRACKET_START_COLOUR
  }
  const page = PLUGIN_COLOUR_START_FROM[name] ?? 'primary'
  return colours[page] ?? defaults[page]
}

const optionalColoursOf = (colours: PluginColours): PluginColours => {
  const kept: PluginColours = {}
  for (const name of OPTIONAL_COLOUR_NAMES) {
    const value = colours[name]
    if (value !== undefined) {
      kept[name] = value
    }
  }
  return kept
}

/**
 * Default unsets the page colours (the plugin's own, so Mode keeps working); a
 * preset replaces them: its three set, background, text and border unset.
 * Optional colours stay.
 */
export const withPluginPreset = (
  colours: PluginColours,
  preset: 'default' | PluginThemePresetName
): PluginColours => ({
  ...(preset === 'default' ? {} : PLUGIN_THEME_PRESETS[preset]),
  ...optionalColoursOf(colours),
})

const PRESET_NAMES = Object.keys(
  PLUGIN_THEME_PRESETS
) as PluginThemePresetName[]

const samePageColours = (
  colours: PluginColours,
  preset: Partial<Record<PluginPageColour, string>>
) =>
  PAGE_COLOUR_NAMES.every(
    (name) => colours[name]?.toUpperCase() === preset[name]?.toUpperCase()
  )

/** The preset the colours match: Default while no page colour is set, a preset when its own are set and the rest unset, else none. */
export const pluginActivePreset = (
  colours: PluginColours
): 'default' | PluginThemePresetName | null => {
  if (samePageColours(colours, {})) {
    return 'default'
  }
  return (
    PRESET_NAMES.find((preset) =>
      samePageColours(colours, PLUGIN_THEME_PRESETS[preset])
    ) ?? null
  )
}

/** The colours with the named ones unset. */
export const withoutPluginColours = (
  colours: PluginColours,
  names: PluginThemeColour[]
): PluginColours => {
  const removed = new Set<PluginThemeColour>(names)
  const kept: PluginColours = {}
  for (const [name, value] of Object.entries(colours) as [
    PluginThemeColour,
    string | undefined
  ][]) {
    if (!removed.has(name) && value !== undefined) {
      kept[name] = value
    }
  }
  return kept
}

/** Turns optional colours on: each unset one starts from its swatch's start colour; set ones stay. */
export const withPluginColoursStarted = (
  colours: PluginColours,
  names: PluginOptionalColour[],
  defaults: Record<PluginPageColour, string>
): PluginColours => {
  const started: PluginColours = { ...colours }
  for (const name of names) {
    started[name] = colours[name] ?? pluginStartColour(colours, name, defaults)
  }
  return started
}

export const pluginBrandForTenant = (isCanlan: boolean): PluginBrand =>
  isCanlan ? 'canlan' : 'sportninja'
